import * as React from 'react'

import { TagColor as Color, Tag } from '@factorialco/deprecated-design-system'
import { F0TagStatus, Variant } from '@factorialco/f0-react'
import {
  DocumentsSignatureStatusEnum,
  PerformanceAgreementStatusEnum,
  PerformanceAgreementStatusTagFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  DOCUMENT_SIGNATURE_BOUNCED_EMAIL,
  DOCUMENT_SIGNATURE_CANCELLED,
  DOCUMENT_SIGNATURE_DECLINED,
  DOCUMENT_SIGNATURE_ERROR,
  DOCUMENT_SIGNATURE_EXPIRED,
  DOCUMENT_SIGNATURE_PARTIALLY_SIGNED,
} from 'modules/documents/lib/constants'

type StatusEnum = PerformanceAgreementStatusEnum | 'not_initiated'

interface AgreementStatusTagProps {
  agreement?: PerformanceAgreementStatusTagFragment | null
  suffix?: string
  f0?: boolean
}

const statusToColor: Record<StatusEnum, Color> = {
  not_initiated: Color.grey,
  pending: Color.malibu,
  signed: Color.correct,
}

const statusToVariant: Record<StatusEnum, Variant> = {
  not_initiated: 'neutral',
  pending: 'warning',
  signed: 'positive',
}

const signatureStatusToColor: Record<DocumentsSignatureStatusEnum, Color> = {
  pending: Color.champagne,
  partially_signed: Color.malibu,
  completed: Color.correct,
  declined: Color.radical,
  bounced_email: Color.radical,
  cancelled: Color.neutral,
  error: Color.radical,
  expired: Color.radical,
}

const signatureStatusToVariant: Record<DocumentsSignatureStatusEnum, Variant> = {
  pending: 'warning',
  partially_signed: 'info',
  completed: 'positive',
  declined: 'critical',
  bounced_email: 'critical',
  cancelled: 'neutral',
  error: 'critical',
  expired: 'critical',
}

export const getStatusLabel = (status: StatusEnum) => {
  switch (status) {
    case 'not_initiated':
      return i18n.t('performance_process.agreements.my_agreements.category_not_started')
    case 'pending':
      return i18n.t('performance_process.agreements.my_agreements.category_pending_submission')
    case 'signed':
      return i18n.t('performance_process.agreements.status.submitted')
    default:
      const _exhaustiveCheck: never = status
      throw new Error(`Unhandled agreement status: ${_exhaustiveCheck}`)
  }
}

export const getSignatureStatusLabel = (status: DocumentsSignatureStatusEnum) => {
  switch (status) {
    case 'pending':
      return i18n.t('performance_process.agreements.status.pending_signatures')
    case 'partially_signed':
      return DOCUMENT_SIGNATURE_PARTIALLY_SIGNED()
    case 'completed':
      return i18n.t('performance_process.agreements.status.signed')
    case 'declined':
      return DOCUMENT_SIGNATURE_DECLINED()
    case 'bounced_email':
      return DOCUMENT_SIGNATURE_BOUNCED_EMAIL()
    case 'cancelled':
      return DOCUMENT_SIGNATURE_CANCELLED()
    case 'error':
      return DOCUMENT_SIGNATURE_ERROR()
    case 'expired':
      return DOCUMENT_SIGNATURE_EXPIRED()
    default:
      const _exhaustiveCheck: never = status
      throw new Error(`Unhandled agreement status: ${_exhaustiveCheck}`)
  }
}

export const getSignatureStatus = (agreement?: PerformanceAgreementStatusTagFragment | null) =>
  agreement?.agreementDocuments?.[0]?.signatureStatus

export const AgreementStatusTag = ({ agreement, suffix, f0 = false }: AgreementStatusTagProps) => {
  const signatureStatus = getSignatureStatus(agreement)
  const agreementStatus = agreement?.status ?? 'not_initiated'

  const label = signatureStatus
    ? getSignatureStatusLabel(signatureStatus)
    : getStatusLabel(agreementStatus)

  if (f0) {
    const variant = signatureStatus
      ? signatureStatusToVariant[signatureStatus]
      : statusToVariant[agreementStatus]

    return <F0TagStatus text={label} variant={variant} />
  }

  const color = signatureStatus
    ? signatureStatusToColor[signatureStatus]
    : statusToColor[agreementStatus]

  return (
    <Tag inline capitalize type={color}>
      {label} {suffix}
    </Tag>
  )
}
